/**
 * The bar that appears once rows are ticked: what is selected, and what can be
 * done to all of it. Actions come from the SEGMENT, so the bar can only ever
 * offer transitions that exist for the rows on screen.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import { queueActionGlyph, queueActionSurface } from '../queue-action-style';
import type { BulkActionDef, BulkActionId } from './bulk-actions';

export interface BulkActionBarProps {
  count: number;
  allSelected: boolean;
  actions: BulkActionDef[];
  running: boolean;
  done: number;
  total: number;
  onAction: (action: BulkActionDef) => void;
  onToggleAll: () => void;
  onClear: () => void;
  onCancel: () => void;
}

const BulkActionBar: FC<BulkActionBarProps> = ({
  count,
  allSelected,
  actions,
  running,
  done,
  total,
  onAction,
  onToggleAll,
  onClear,
  onCancel,
}) => {
  if (count === 0 && !running) return null;
  return (
    <div
      data-testid="bulk-action-bar"
      className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/[0.06] px-2 py-1.5 backdrop-blur"
    >
      {/* Clearing the selection belongs WITH the count it clears — and keeping
       *  it out of the trailing group leaves the actions flush right, directly
       *  above each row's own buttons. */}
      {!running && (
        <button
          type="button"
          onClick={onClear}
          title="Clear selection"
          className="rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      <span className="text-xs font-semibold tabular-nums">
        {running ? `Working… ${done}/${total}` : `${count} selected`}
      </span>
      {running ? (
        <Button variant="outline" size="sm" className="h-7" onClick={onCancel}>
          Stop after this one
        </Button>
      ) : (
        <>
          {/* A button, not an underlined link: it sits in a row of buttons and
           *  does the same kind of thing they do — changing the selection. */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={onToggleAll}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </Button>
          <span className="ml-auto flex flex-wrap items-center gap-1.5">
            {actions.map((a) => (
              // Eliminate stays SOLID red here rather than taking the row
              // buttons' tint: this one fires on every ticked row at once, and
              // the loudest thing in the bar should be the irreversible one.
              <Button
                key={a.id satisfies BulkActionId}
                size="sm"
                variant={a.destructive ? 'destructive' : 'outline'}
                className={cn(
                  'h-7 gap-1 text-[11px]',
                  !a.destructive && queueActionSurface(a.tone),
                )}
                onClick={() => onAction(a)}
              >
                <a.icon
                  className={cn(
                    'h-3 w-3 shrink-0',
                    !a.destructive && queueActionGlyph(a.tone),
                  )}
                  aria-hidden
                />
                {a.label}
              </Button>
            ))}
          </span>
        </>
      )}
    </div>
  );
};

export default BulkActionBar;
